'use client';

/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Card } from '@repo/ux/card';
import ErrorDialog from '@/components/Dialog/ErrorDialog';
import InputPasswordStrengthDemo from '@/components/ux/password-strength-input';
import { useSignupMutation } from '@/graphql';
import { dateToString, minimumBirthDate } from '@/helpers/datetime';
import { getAuthToken, setUseridToken } from '@/lib/auth';
import { passwordSchema } from '@/lib/password-rules';
import { birthdateSchema, DatePicker } from '@/ui/dob-picker';
import { ApolloErrorHandler } from '@/utils/errorHandling';

import { useAnalytics } from '../../../../hooks/useAnalytics';
import Password from '../Password';

// Calculate the minimum date (13 years ago from today)
const minDateOfBirth = minimumBirthDate();

// Define validation schema using Yup
const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  birthdate: birthdateSchema.max(
    minDateOfBirth,
    'You must be at least 13 years old'
  ),
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
  email_confirmation: Yup.string()
    .oneOf([Yup.ref('email')], 'Emails must match')
    .required('Email is required'),
  password: passwordSchema(),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Password confirmation is required'),
});

// Form data type
interface IRegisterFormInputs {
  first_name: string;
  last_name: string;
  birthdate: Date;
  email: string;
  email_confirmation: string;
  password: string;
  password_confirmation: string;
}

const RegisterForm = ({ userType }: { userType: string }) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'error' | 'warning' | 'info'>(
    'error'
  );
  const router = useRouter();
  const {
    trackButton,
    trackForm,
    trackError,
    trackFunnelStep,
    setFunnelSession,
  } = useAnalytics();

  const searchParams = useSearchParams();
  const hk = searchParams.get('hk');
  const redirect = searchParams.get('redirect');

  // Track funnel Step 2
  useEffect(() => {
    if (hk) {
      trackFunnelStep('account_creation', 2, hk, {
        user_type: userType,
      });
      setFunnelSession(hk);
    }
  }, [hk, userType, trackFunnelStep, setFunnelSession]);

  const [signup, { data, loading, error }] = useSignupMutation({
    onCompleted: (data) => {
      setUseridToken(data?.signup?.user?.id || '');
      router.push(
        hk ? `/verify?hk=${hk}&usr=${userType}` : '/verify?usr=${userType}'
      );
    },
    onError: (error) => {
      const errorResponse = ApolloErrorHandler.handle(error);

      // Determine error type based on error message or code
      let errorType: 'error' | 'warning' | 'info' = 'error';

      // Example: If it's a network error, mark it as warning
      if (error.networkError) {
        errorType = 'warning';
      }

      // Example: If it's a validation error, mark it as info
      if (errorResponse.message?.toLowerCase().includes('validation')) {
        errorType = 'info';
      }

      setErrorMessage(errorResponse.message);
      setErrorType(errorType);
      setOpen(true);
      ApolloErrorHandler.logError(error);
    },
  });

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IRegisterFormInputs>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      birthdate: undefined,
    },
  });

  const onSubmit = async (data: IRegisterFormInputs) => {
    setErrorMessage(null); // Reset error message on form submit
    trackButton('register_form_submit', 'signup_form');
    // check if logged in already
    const token = getAuthToken();
    if (token) {
      let targetPath = '';
      if (hk) {
        targetPath = `/${userType === 'team' ? 'medical-team' : 'participant'}/register/event/${hk}`;
      } else {
        targetPath = `/${userType === 'team' ? 'medical-team' : 'participant'}/dashboard`;
      }

      window.location.href = redirect || targetPath;
      return;
    }
    trackForm('register_form', hk || undefined);
    trackFunnelStep('account_created', 2, hk || undefined, {
      auth_method: 'email',
      user_type: userType,
    });

    signup({
      variables: {
        first_name: data.first_name,
        last_name: data.last_name,
        dob: dateToString(data.birthdate),
        email: data.email,
        password: data.password,
      },
    });
  };

  const onValidationFailure = (errors: FieldErrors<IRegisterFormInputs>) => {
    // Iterate over the errors object and send analytics
    for (const fieldName in errors) {
      if (errors.hasOwnProperty(fieldName)) {
        const error = errors[fieldName as keyof IRegisterFormInputs];
        if (error?.message) {
          trackError(error.message, 'validation_error');
        }
      }
    }
  };

  // report submission errors to analytics
  useEffect(() => {
    if (errorMessage) {
      trackError(errorMessage, errorType);
    }
  }, [trackError, errorType, errorMessage]);

  return (
    <Card className="w-full p-5 text-black dark:border-black dark:bg-black dark:text-white md:min-w-125">
      <div className="flex flex-col">
        <div className="mb-10 mt-2 rounded-sm border border-spkblue-base_color bg-spkblue-base_color/10 p-5">
          Already have an account?{' '}
          <Link
            href={
              hk
                ? `/${userType === 'team' ? 'medical-team' : 'participant'}/login?hk=${hk}&usr=${userType}`
                : '/participant/login'
            }
            className="text-spkblue-base_color hover:underline"
          >
            Log in here
          </Link>
        </div>
        <h2 className="text-3xl text-black dark:text-white">
          Create your account
        </h2>

        {/* <div className="my-5 flex flex-col gap-4">
          <button
            type="button"
            className="inline-flex w-full items-center justify-center gap-x-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 shadow-xs hover:bg-gray-50 focus:bg-gray-50 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:focus:bg-neutral-800"
          >
            <svg
              className="size-4 shrink-0"
              width="33"
              height="32"
              viewBox="0 0 33 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_4132_5805adfqfqdq121)">
                <path
                  d="M32.2566 16.36C32.2566 15.04 32.1567 14.08 31.9171 13.08H16.9166V19.02H25.7251C25.5454 20.5 24.5866 22.72 22.4494 24.22L22.4294 24.42L27.1633 28.1L27.4828 28.14C30.5189 25.34 32.2566 21.22 32.2566 16.36Z"
                  fill="#4285F4"
                ></path>
                <path
                  d="M16.9166 32C21.231 32 24.8463 30.58 27.5028 28.12L22.4694 24.2C21.1111 25.14 19.3135 25.8 16.9366 25.8C12.7021 25.8 9.12677 23 7.84844 19.16L7.66867 19.18L2.71513 23L2.65521 23.18C5.2718 28.4 10.6648 32 16.9166 32Z"
                  fill="#34A853"
                ></path>
                <path
                  d="M7.82845 19.16C7.48889 18.16 7.28915 17.1 7.28915 16C7.28915 14.9 7.48889 13.84 7.80848 12.84V12.62L2.81499 8.73999L2.6552 8.81999C1.55663 10.98 0.937439 13.42 0.937439 16C0.937439 18.58 1.55663 21.02 2.63522 23.18L7.82845 19.16Z"
                  fill="#FBBC05"
                ></path>
                <path
                  d="M16.9166 6.18C19.9127 6.18 21.9501 7.48 23.0886 8.56L27.6027 4.16C24.8263 1.58 21.231 0 16.9166 0C10.6648 0 5.27181 3.6 2.63525 8.82L7.80851 12.84C9.10681 8.98 12.6821 6.18 16.9166 6.18Z"
                  fill="#EB4335"
                ></path>
              </g>
              <defs>
                <clipPath id="clip0_4132_5805adfqfqdq121">
                  <rect
                    width="32"
                    height="32"
                    fill="white"
                    transform="translate(0.937439)"
                  ></rect>
                </clipPath>
              </defs>
            </svg>
            Sign in with Google
          </button>

          <button
            type="button"
            className="inline-flex w-full items-center justify-center gap-x-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 shadow-xs hover:bg-gray-50 focus:bg-gray-50 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:focus:bg-neutral-800"
          >
            <svg
              className="size-4 shrink-0"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M27.2192 10.9088C27.0336 11.0528 23.7568 12.8992 23.7568 17.0048C23.7568 21.7536 27.9264 23.4336 28.0512 23.4752C28.032 23.5776 27.3888 25.776 25.8528 28.016C24.4832 29.9872 23.0528 31.9552 20.8768 31.9552C18.7008 31.9552 18.1408 30.6912 15.6288 30.6912C13.1808 30.6912 12.3104 31.9968 10.32 31.9968C8.3296 31.9968 6.9408 30.1728 5.344 27.9328C3.4944 25.3024 2 21.216 2 17.3376C2 11.1168 6.0448 7.8176 10.0256 7.8176C12.1408 7.8176 13.904 9.2064 15.232 9.2064C16.496 9.2064 18.4672 7.7344 20.8736 7.7344C21.7856 7.7344 25.0624 7.8176 27.2192 10.9088ZM19.7312 5.1008C20.7264 3.92 21.4304 2.2816 21.4304 0.6432C21.4304 0.416 21.4112 0.1856 21.3696 0C19.7504 0.0608 17.824 1.0784 16.6624 2.4256C15.7504 3.4624 14.8992 5.1008 14.8992 6.7616C14.8992 7.0112 14.9408 7.2608 14.96 7.3408C15.0624 7.36 15.2288 7.3824 15.3952 7.3824C16.848 7.3824 18.6752 6.4096 19.7312 5.1008Z"
                className="fill-black dark:fill-neutral-200"
                fill="currentColor"
              ></path>
            </svg>
            Sign in with Apple
          </button>
        </div>

        <div className="my-3 flex items-center py-3 text-xs uppercase text-gray-400 before:me-6 before:flex-1 before:border-t before:border-gray-200 after:ms-6 after:flex-1 after:border-t after:border-gray-200 dark:text-neutral-500 dark:before:border-neutral-600 dark:after:border-neutral-600">
          Or
        </div> */}

        <form
          className="flex flex-col mt-2 gap-2"
          onSubmit={handleSubmit(onSubmit, onValidationFailure)}
        >
          <div>
            <DatePicker
              label={'Birth Date'}
              value={watch('birthdate')}
              onChange={(date) =>
                setValue('birthdate', date as Date, {
                  shouldValidate: true,
                })
              }
              className={classNames({ 'border-red': errors.birthdate })}
              errors={errors.birthdate as any}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium dark:text-white">
              First Name
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('first_name')}
                id="hs-validation-name-error"
                placeholder="Enter your first name"
                className={classNames(
                  'block w-full rounded-lg border-gray-200 px-4 py-3 transition-all duration-200 text-form-input focus:border-spkblue-base_color focus:ring-spkblue-base_color disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400',
                  {
                    'border-red-500 focus:border-red-500 focus:ring-red-500':
                      errors.first_name,
                  }
                )}
                aria-describedby="hs-validation-name-error-helper"
              />
              {errors.first_name && (
                <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3">
                  <svg
                    className="size-4 shrink-0 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                </div>
              )}
            </div>
            {errors.first_name && (
              <p
                className="mt-2 text-sm text-red-600"
                id="hs-validation-name-error-helper"
              >
                {errors.first_name.message}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium dark:text-white">
              Last Name
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('last_name')}
                id="hs-validation-name-error"
                placeholder="Enter your last name"
                className={classNames(
                  'block w-full rounded-lg border-gray-200 px-4 py-3 transition-all duration-200 text-form-input focus:border-spkblue-base_color focus:ring-spkblue-base_color disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400',
                  {
                    'border-red-500 focus:border-red-500 focus:ring-red-500':
                      errors.last_name,
                  }
                )}
                aria-describedby="hs-validation-name-error-helper"
              />
              {errors.last_name && (
                <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3">
                  <svg
                    className="size-4 shrink-0 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                </div>
              )}
            </div>
            {errors.last_name && (
              <p
                className="mt-2 text-sm text-red-600"
                id="hs-validation-name-error-helper"
              >
                {errors.last_name.message}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium dark:text-white">
              Email
            </label>
            <div className="relative">
              <input
                type="email"
                {...register('email')}
                id="hs-validation-name-error"
                placeholder="Enter your email"
                className={classNames(
                  'block w-full rounded-lg border-gray-200 px-4 py-3 transition-all duration-200 text-form-input focus:border-spkblue-base_color focus:ring-spkblue-base_color disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400',
                  {
                    'border-red-500 focus:border-red-500 focus:ring-red-500':
                      errors.email,
                  }
                )}
                aria-describedby="hs-validation-name-error-helper"
              />
              {errors.email && (
                <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3">
                  <svg
                    className="size-4 shrink-0 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                </div>
              )}
            </div>
            {errors.email && (
              <p
                className="mt-2 text-sm text-red-600"
                id="hs-validation-name-error-helper"
              >
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium dark:text-white">
              Email Confirmation
            </label>
            <div className="relative">
              <input
                type="email"
                {...register('email_confirmation')}
                id="hs-validation-name-error"
                placeholder="Enter your email"
                className={classNames(
                  'block w-full rounded-lg border-gray-200 px-4 py-3 transition-all duration-200 text-form-input focus:border-spkblue-base_color focus:ring-spkblue-base_color disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400',
                  {
                    'border-red-500 focus:border-red-500 focus:ring-red-500':
                      errors.email_confirmation,
                  }
                )}
                aria-describedby="hs-validation-name-error-helper"
              />
              {errors.email_confirmation && (
                <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center pe-3">
                  <svg
                    className="size-4 shrink-0 text-red-500"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                </div>
              )}
            </div>
            {errors.email_confirmation && (
              <p
                className="mt-2 text-sm text-red-600"
                id="hs-validation-name-error-helper"
              >
                {errors.email_confirmation.message}
              </p>
            )}
          </div>

          <InputPasswordStrengthDemo
            label="Password"
            error={errors.password}
            register={register('password')}
          />

          <Password
            label="Password Confirmation"
            id="hs-toggle-password-confirmation"
            errors={errors.password_confirmation}
            register={register('password_confirmation')}
          />

          <button
            disabled={loading}
            className={classNames(
              'mt-5 inline-flex w-full items-center justify-center gap-x-2 rounded-lg border border-transparent bg-spkblue-base_color px-4 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-spkblue-base_color2 focus:bg-spkblue-base_color2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-50',
              { 'cursor-not-allowed': loading }
            )}
          >
            {loading && (
              <span
                className="inline-block size-4 animate-spin rounded-full border-[3px] border-current border-t-transparent text-white"
                role="status"
                aria-label="loading"
              ></span>
            )}
            Continue
          </button>
          {/* <Button className='mt-5' label='Continue' color="red" showActivity={loading}/> */}
        </form>

        {/* <div className='my-5 mt-10'>
              <SocialButton className='text-black' imgAlt= 'Apple Social Icon' imgURL={applelogo.src} label= 'Continue with Apple' onClick={()=> null} primary={false} />
              <SocialButton className='text-black' imgAlt= 'Google Social Icon' imgURL={googlelogo.src} label= 'Continue with Google' onClick={()=> null} primary={false} />
            </div> */}

        <div className="mt-10 flex flex-col gap-2">
          <span>
            Read{' '}
            <Link
              className="text-spkblue-base_color hover:underline"
              href="/privacy/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </Link>{' '}
            and{' '}
            <Link
              className="text-spkblue-base_color hover:underline"
              href="/privacy/policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>

      {/* Error Dialog */}
      <ErrorDialog
        isOpen={open}
        setIsOpen={setOpen}
        message={errorMessage}
        type={errorType}
        buttonText="Close"
        autoClose={errorType === 'info'} // Auto-close only for info messages
        autoCloseTime={6000}
      />
    </Card>
  );
};

export default RegisterForm;
